/* @jsxImportSource @emotion/react */
import React from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import { SlitePage } from '../../components/SlitePage'
import type { LegalDocKey } from '../../data/legalDocs'
import { useRoutes } from '../../routes'
import { HelpHtml } from '../help/HelpHtml'
import type { Data } from './+data'

const ARTICLE_WIDTH = 728

function metaDescription(intl: IntlShape, doc: LegalDocKey): string {
  switch (doc) {
    case 'legalInformation':
      return intl.formatMessage({
        id: 'website.legal.legalInformation.helmet.description',
        defaultMessage:
          'Legal information about Slite, Inc. — service operator, European entity, hosting provider, and privacy resources.',
      })
    case 'privacy':
      return intl.formatMessage({
        id: 'website.legal.privacy.helmet.description',
        defaultMessage:
          'How Slite collects, uses, and protects personal information across our website, apps, and services.',
      })
    case 'security':
      return intl.formatMessage({
        id: 'website.legal.security.helmet.description',
        defaultMessage:
          'Slite’s security practices — SOC 2 Type II compliance, GDPR, data hosting, encryption, and operational security.',
      })
  }
}

export default function LegalDocPage() {
  const { doc, note } = useData<Data>()
  const intl = useIntl()
  const routes = useRoutes()

  return (
    <SlitePage
      title={intl.formatMessage(
        {
          id: 'website.legal.helmet.title',
          defaultMessage: '{title} | Slite',
        },
        { title: note.title }
      )}
      description={metaDescription(intl, doc)}
      canonicalUrl={routes.legal[doc]()}
      background="shade-100"
    >
      <main
        css={{
          maxWidth: ARTICLE_WIDTH,
          margin: '0 auto',
          padding: '40px 24px 120px',
        }}
      >
        <h1
          data-test-id="legalDocTitle"
          className="font-family-title shade-05"
          css={{
            margin: '0 0 12px',
            fontSize: 40,
            fontWeight: 600,
            lineHeight: '52px',
            letterSpacing: '-0.6px',
          }}
        >
          {note.title}
        </h1>
        <HelpHtml html={note.html} />
      </main>
    </SlitePage>
  )
}
